import {markRaw} from 'vue';
import {getFile} from '../../services/CloudStorageService.js';
import {gmAPI} from '../../services/MapService';
import {Graphic} from './Graphic.js';
import {mapsAddListenerMixin} from '@/lib/classes/EventHandling.js';

/**
 * @typedef {object} GeoJSONData
 * @property {string[]} colors An array of fill colors to style this graphic.
 * @property {string} [geoJson] The raw GeoJSON data to construct this graphic.
 * @property {string} [imagePath] The URL from which to download GeoJSON data.
 * @property {google.maps.Data.StyleOptions} [style]
 * An optional object with styles to be applied to this graphic's DataLayer via setStyle().
 */

/** @typedef {import('./GraphicService').GraphicData & GeoJSONData} GeoJSONGraphicData */

/**
 * @private
 * @type {google.maps.Data.StyleOptions}
 */
const DEFAULT_STYLE = {
  clickable: false,
  cursor: 'grab',
  strokeColor: 'black',
  strokeWeight: 1,
};

/**
 * Asynchronously constructs a GeoJSONGraphic object.
 * @async There may be a delay if the feature contains an `imagePath` from which to download GeoJSON.
 * @param {GeoJSONGraphicData} data The data object from which to produce the graphic.
 * @return {GeoJSONGraphic} The constructed graphic object.
 */
async function createGeoJSONGraphic(data) {
  const geoJson = data.geoJson ?? await downloadGeoJson(data.imagePath);
  return new GeoJSONGraphic({...data, geoJson});
}

/**
 * Downloads and parses raw GeoJSON from Google Cloud Storage to be used for a GeoJSONGraphic.
 * @private
 * @async
 * @param {string} path A path to a Google Cloud Storage file containing GeoJSON.
 * @return {string} The retrieved and parsed GeoJSON.
 */
async function downloadGeoJson(path) {
  const blob = await getFile(path);
  const content = await blob.text();
  const geoJson = JSON.parse(content);
  return geoJson;
}
class GeoJSONGraphic extends Graphic {
  /**
   * Constructs a new GeoJSONGraphic.
   * @param {GeoJSONGraphicData} data The data from which to generate the graphic.
   */
  constructor({colors, geoJson, style}) {
    super();
    if (!colors) {
      throw new Error('Error constructing GeoJSONGraphic: colors must be supplied.');
    }
    /** @type {string[]} */
    this.colors = colors;
    if (!geoJson) {
      throw new Error('Error constructing GeoJSONGraphic: geoJson must be supplied.');
    }
    /** @type {string} */
    this.geoJson = geoJson;
    /** @type {google.maps.Data.StyleOptions} */
    this.style = {...DEFAULT_STYLE, ...style};
    /** @type {google.maps.Data} */
    this.dataLayer = markRaw(new gmAPI.Data({map: null}));
    this.dataLayer.addGeoJson(this.geoJson);
    this.dataLayer.setStyle((feature) => this.stylingFunction(feature));
  }

  /**
   * Sets whether this graphic can be clicked.
   * @param {boolean} clickable If `true`, this overlay will be clickable on the map; otherwise it will not.
   */
  setClickable(clickable) {
    this.style.clickable = clickable;
  }

  isClickable() {
    return this.style.clickable;
  }

  /**
   * Attaches the graphic to a map or removes it from a map.
   * @param {google.maps.Map|null} map The map to attach this graphic to,
   * or `null` to detach the graphic from its current map.
   */
  setMap(map) {
    this.dataLayer.setMap(map);
  }

  /**
   * A function to determine what styles should be applied to a child feature of this graphic.
   * @param {google.maps.Data.Feature} feature The feature to which styles should be applied.
   * @return {google.maps.Data.StyleOptions}
   */
  stylingFunction(feature) {
    return {
      fillColor: this.colors[feature.getId() - 1], // feature.getId() returns the id of the feature (1, 2, 3, etc.)
      ...this.style,
    };
  }
}

Object.assign(GeoJSONGraphic.prototype, mapsAddListenerMixin('dataLayer'));

export {createGeoJSONGraphic, GeoJSONGraphic};
